import { join, dirname } from 'path';
import fs from 'fs-extra';
import chalk from 'chalk';
import { logger } from './logger.js';
import {
  computeHash,
  ensureStep,
  findKnownHash,
  recordFile,
  type InitState,
} from './file-tracker.js';

// ============================================================================
// Docker file synchronization (ss upgrade)
//
// Replaces the historical unconditional overwrite with the safeWriteFile
// semantics: a file is only rewritten when it is PROVEN pristine — either its
// hash is recorded in .smartstack/init-state.json, or its content matches a
// historical rendition of the template (templates/project/legacy/). A file the
// client edited by hand matches neither → it is skipped, listed, and only
// --force-docker (with a .bak backup) overwrites it.
//
// The legacy variant set is FROZEN: every sync since this mechanism records
// the written hash into init-state, so no future template version ever needs
// to be archived here. Only files deposited by CLIs that predate the tracking
// (docker templates appeared in 4.55.0–5.7.0; the old sync never recorded
// anything) can exist on a client disk without a known hash.
// ============================================================================

export interface DockerSyncFileSpec {
  /** Template file name under templatesDir (templates/project/) */
  template: string;
  /** POSIX-style path of the generated file, relative to the project root */
  relPath: string;
  /** Historical renditions under templatesDir/legacy/ (frozen set) */
  legacyVariants: readonly string[];
}

export const DOCKER_SYNC_FILES: readonly DockerSyncFileSpec[] = [
  {
    template: 'Dockerfile.backend.template',
    relPath: 'docker-images/Dockerfile.backend',
    legacyVariants: [
      'Dockerfile.backend.9f50ff7.template',
      'Dockerfile.backend.00ae6b0.template',
      'Dockerfile.backend.0ece058.template',
      'Dockerfile.backend.979fee0.template',
      'Dockerfile.backend.7522757.template',
      'Dockerfile.backend.42fed61.template',
    ],
  },
  {
    template: 'Dockerfile.frontend.template',
    relPath: 'docker-images/Dockerfile.frontend',
    legacyVariants: [
      'Dockerfile.frontend.b626906.template',
      'Dockerfile.frontend.0d4ce95.template',
      'Dockerfile.frontend.f7c66c4.template',
      'Dockerfile.frontend.b3f23ae.template',
      'Dockerfile.frontend.42fed61.template',
    ],
  },
  {
    template: 'nginx-frontend.conf.template',
    relPath: 'docker-images/nginx-frontend.conf',
    legacyVariants: [
      'nginx-frontend.conf.cf29118.template',
      'nginx-frontend.conf.b626906.template',
    ],
  },
  {
    template: 'dockerignore.template',
    relPath: '.dockerignore',
    legacyVariants: ['dockerignore.42fed61.template'],
  },
  {
    template: 'docker-compose.yml.template',
    relPath: 'docker-images/docker-compose.yml',
    legacyVariants: [
      'docker-compose.yml.9f50ff7.template',
      'docker-compose.yml.2a9a7ce.template',
    ],
  },
];

export type DockerFileAction =
  | 'created'
  | 'updated'          // pristine via init-state hash → overwritten
  | 'adopted'          // pristine via a historical template rendition → overwritten
  | 'forced'           // --force-docker → .bak backup + overwritten
  | 'skipped_modified' // locally modified → untouched
  | 'up_to_date';      // identical (modulo EOL) → untouched

export interface DockerFileSyncResult {
  relPath: string;
  action: DockerFileAction;
  /** Set for 'forced': project-relative path of the backup */
  backupPath?: string;
}

export interface DockerSyncSummary {
  files: DockerFileSyncResult[];
  /** created + updated + adopted + forced */
  written: number;
  /** relPaths left untouched because they were modified locally */
  skippedModified: string[];
  /** true when at least one hash was recorded into the state */
  stateChanged: boolean;
}

export interface DockerSyncOptions {
  projectDir: string;
  /** Project name used to render {{ProjectName}} / {{ProjectNameLower}} */
  projectName: string;
  /** templates/project directory (injected → testable) */
  templatesDir: string;
  /** Mutated via recordFile; caller persists it when stateChanged */
  state: InitState;
  dryRun: boolean;
  force: boolean;
  /** Optional template blocks to render (see applyTemplateBlocks); absent = off. */
  blocks?: TemplateBlocks;
}

/** Normalize CRLF to LF — client git core.autocrlf must not read as "modified". */
export function normalizeEol(content: string): string {
  return content.replace(/\r\n/g, '\n');
}

/**
 * Render the two project placeholders — the exact mirror of init.ts
 * loadTemplate. NEVER use resolveTemplatePlaceholders here: its {{JwtSecret}}
 * is random per call, which would make any content comparison
 * non-deterministic if a docker template ever gained one.
 */
export function renderProjectTemplate(content: string, projectName: string): string {
  return content
    .replace(/\{\{ProjectName\}\}/g, projectName)
    .replace(/\{\{ProjectNameLower\}\}/g, projectName.toLowerCase());
}

/** Optional template blocks, keyed by block name. Absent = off. */
export type TemplateBlocks = Readonly<Record<string, boolean>>;

const BLOCK_RE = /^[ \t]*#[ \t]*\{\{#(\w+)\}\}[ \t]*\r?\n([\s\S]*?)^[ \t]*#[ \t]*\{\{\/\1\}\}[ \t]*(?:\r?\n|$)/gm;

/**
 * Keep or drop the optional blocks of a template. A block is delimited by two
 * YAML comment lines — `# {{#Name}}` and `# {{/Name}}` — so the raw template
 * stays valid YAML; both marker lines always disappear from the output, the
 * body is kept only when `blocks[Name]` is true. Deterministic: the docker sync
 * hash-compares the result.
 */
export function applyTemplateBlocks(content: string, blocks: TemplateBlocks): string {
  return content.replace(BLOCK_RE, (_match, name: string, body: string) => (blocks[name] ? body : ''));
}

/**
 * Compose block carrying the previous-certificate slot. Only for SmartStack ≥
 * 3.70: an EMPTY AdditionalCertificates entry fails startup before it.
 */
export const ADDITIONAL_CERTIFICATE_SLOT_BLOCK = 'AdditionalCertificateSlot';

/**
 * Synchronize the generated docker files with the current templates without
 * ever silently destroying a client's local modifications.
 */
export async function syncDockerFiles(opts: DockerSyncOptions): Promise<DockerSyncSummary> {
  const { projectDir, projectName, templatesDir, state, dryRun, force, blocks = {} } = opts;

  ensureStep(state, 'docker', 'Docker configuration');

  const files: DockerFileSyncResult[] = [];
  let stateChanged = false;

  const record = (relPath: string, hash: string): void => {
    if (dryRun) return;
    recordFile(state, 'docker', relPath, hash);
    stateChanged = true;
  };

  for (const spec of DOCKER_SYNC_FILES) {
    const templatePath = join(templatesDir, spec.template);
    if (!await fs.pathExists(templatePath)) {
      logger.debug(`${spec.template} not found, skipping`);
      continue;
    }

    const desired = renderProjectTemplate(applyTemplateBlocks(await fs.readFile(templatePath, 'utf-8'), blocks), projectName);
    const desiredHash = computeHash(desired);
    const desiredNormHash = computeHash(normalizeEol(desired));
    const destPath = join(projectDir, ...spec.relPath.split('/'));

    // Missing → create
    if (!await fs.pathExists(destPath)) {
      if (!dryRun) {
        await fs.ensureDir(dirname(destPath));
        await fs.writeFile(destPath, desired, 'utf-8');
      }
      record(spec.relPath, desiredHash);
      files.push({ relPath: spec.relPath, action: 'created' });
      continue;
    }

    const disk = await fs.readFile(destPath, 'utf-8');
    const diskHash = computeHash(disk);
    const diskNormHash = computeHash(normalizeEol(disk));

    // Identical modulo EOL → no rewrite (no git churn on CRLF checkouts), but
    // self-heal the baseline so the next run has a known hash.
    if (diskNormHash === desiredNormHash) {
      if (findKnownHash(state, 'docker', spec.relPath) !== desiredHash) {
        record(spec.relPath, desiredHash);
      }
      files.push({ relPath: spec.relPath, action: 'up_to_date' });
      continue;
    }

    // Pristine via init-state baseline?
    const known = findKnownHash(state, 'docker', spec.relPath);
    let pristineVia: 'known' | 'legacy' | null =
      known !== undefined && (known === diskHash || known === diskNormHash) ? 'known' : null;

    // Pristine via a historical rendition of the template?
    if (!pristineVia) {
      for (const variant of spec.legacyVariants) {
        const variantPath = join(templatesDir, 'legacy', variant);
        if (!await fs.pathExists(variantPath)) {
          logger.debug(`legacy variant missing: ${variant}`);
          continue;
        }
        const renderedHash = computeHash(
          normalizeEol(renderProjectTemplate(await fs.readFile(variantPath, 'utf-8'), projectName)),
        );
        if (renderedHash === diskNormHash) {
          pristineVia = 'legacy';
          break;
        }
      }
    }

    if (pristineVia) {
      if (!dryRun) {
        await fs.writeFile(destPath, desired, 'utf-8');
      }
      record(spec.relPath, desiredHash);
      files.push({ relPath: spec.relPath, action: pristineVia === 'known' ? 'updated' : 'adopted' });
      continue;
    }

    if (force) {
      const backupRelPath = `${spec.relPath}.bak`;
      if (!dryRun) {
        await fs.copyFile(destPath, `${destPath}.bak`);
        await fs.writeFile(destPath, desired, 'utf-8');
      }
      record(spec.relPath, desiredHash);
      files.push({ relPath: spec.relPath, action: 'forced', backupPath: backupRelPath });
      continue;
    }

    // Locally modified → NEVER overwrite silently
    files.push({ relPath: spec.relPath, action: 'skipped_modified' });
  }

  const written = files.filter(f =>
    f.action === 'created' || f.action === 'updated' || f.action === 'adopted' || f.action === 'forced',
  ).length;
  const skippedModified = files.filter(f => f.action === 'skipped_modified').map(f => f.relPath);

  return { files, written, skippedModified, stateChanged };
}

/** Per-file logging, mirroring logSafeWriteResult's tone. */
export function logDockerFileResult(result: DockerFileSyncResult, dryRun: boolean): void {
  const prefix = dryRun ? '[DRY RUN] ' : '';
  switch (result.action) {
    case 'created':
      logger.success(`${prefix}${dryRun ? 'Would create' : 'Created'} ${result.relPath}`);
      break;
    case 'updated':
      logger.info(`${prefix}${dryRun ? 'Would update' : 'Updated'} ${result.relPath}`);
      break;
    case 'adopted':
      logger.info(`${prefix}${dryRun ? 'Would update' : 'Updated'} ${result.relPath} (pristine file adopted from a previous CLI version)`);
      break;
    case 'forced':
      logger.warning(`${prefix}${dryRun ? 'Would overwrite' : 'Overwrote'} ${result.relPath} (backup: ${result.backupPath})`);
      break;
    case 'skipped_modified':
      logger.warning(`${prefix}${dryRun ? 'Would skip' : 'Skipped'} ${result.relPath} (modified locally${dryRun ? ' — use --force-docker to overwrite' : ''})`);
      break;
    case 'up_to_date':
      // Silent - identical files need no line
      break;
  }
}

/** Grouped warning listing every divergent file and the escape hatch. */
export function logSkippedModifiedSummary(skippedModified: string[]): void {
  if (skippedModified.length === 0) return;
  logger.warning(
    `${skippedModified.length} docker file(s) differ from the CLI templates and were NOT updated (your changes are preserved):`,
  );
  for (const relPath of skippedModified) {
    console.log(chalk.yellow(`    - ${relPath}`));
  }
  console.log(
    chalk.yellow(`  Review the differences, or overwrite them with: `) +
    chalk.cyan('ss upgrade --force-docker') +
    chalk.yellow(' (a .bak backup of each file is kept)'),
  );
}
